// Full-document render of the tempurpedic ProductListFilterPage: PageSkeleton
// (the React document shell) wrapping the body in order (normal site chrome;
// no header/footer/body_class overrides):
//
// 1. The raw hero banner (rendered only when `hero` is non-null, i.e. the
//    page has a hero_image): raw file-URL inline background-image (never a
//    rendition), h6 category title, h1 with the |richtext-expanded title
//    (`<div class="rich-text">` nested inside the h1 -- preserved as-is),
//    plain-text description, and the badge image container -- which renders
//    WHENEVER the hero does (only the img inside is conditional on the
//    badge/its image being set). The badge renders as an eager `<img>` of the
//    `original` rendition (the platform-wide lazysizes -> eager image
//    convention).
// 2. The `top_banner` Toolbox stream, then the `body` Toolbox stream, each
//    rendered through the SAME ProductListFilterPageBody component in
//    Fragment mode (no w-block wrappers), dispatched through
//    buildToolboxPageMap with the merged id-keyed SSR side-channels.
import React from "react";

import { templates } from "@reactivated";

import { PageSkeleton } from "../components/PageSkeleton";
import { ProductListFilterPageBody } from "../components/ProductListFilterPageBody";

// Mutable reactivated props (not Immutable<>): this template composes the
// in-tree SiteHeader chain via PageSkeleton, which is typed with mutable
// reactivated props (see PageSkeleton).
type IProps = templates.ProductListFilterPageTemplate;

const Hero = (props: {
    hero: NonNullable<IProps["hero"]>;
}): React.JSX.Element => {
    const { hero } = props;
    return (
        <div
            className="hero product-list-hero product-list-hero--pillows l-full-width"
            style={{ backgroundImage: `url(${hero.background_image_url})` }}
        >
            <div className="l-capped-width">
                <div className="product-list-hero__content-container product-list-hero__content-container--center">
                    <h6 className="product-list-hero__category product-list-hero__category--pillows-grid">
                        {hero.category_title}
                    </h6>
                    {/* CMS CharField piped through |richtext (rich-text div
                        wrapper inside the h1). */}
                    <h1
                        className="product-list-hero__title product-list-hero__title--pillow product-list-hero__title--pillows-grid"
                        dangerouslySetInnerHTML={{ __html: hero.title_html }}
                    />
                    <p className="product-list-hero__description product-list-hero__description--pillows-grid">
                        {hero.description}
                    </p>
                </div>
                <div className="product-list-hero-badge__image-container">
                    {hero.badge_image_url && (
                        <img
                            alt={hero.badge_image_alt}
                            src={hero.badge_image_url}
                            className="responsive-img"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export const Template = (props: IProps): React.JSX.Element => {
    const ssr = {
        hardcoded_ssr: props.hardcoded_ssr,
        hotspot_ssr: props.hotspot_ssr,
        breadcrumbs_ssr: props.breadcrumbs_ssr,
        customer_service_ssr: props.customer_service_ssr,
        shop_compare_ssr: props.shop_compare_ssr,
        tab_feature_attribute_ssr: props.tab_feature_attribute_ssr,
        reviews_ssr: props.reviews_ssr,
    };
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            {props.hero && <Hero hero={props.hero} />}
            <ProductListFilterPageBody
                page={props.page}
                field="top_banner"
                {...ssr}
            />
            <ProductListFilterPageBody
                page={props.page}
                field="body"
                {...ssr}
            />
        </PageSkeleton>
    );
};
